import { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { SRGBColorSpace, VideoTexture } from 'three';

// The journey's nebula beat as a Grok video inside the WebGL world:
// two feather-edged planes (no rectangular video border in space) with a
// double-buffered crossfade for a natural loop, and distance-based
// play/pause so the decoder is idle except around the beat (Iris Xe).
const CROSSFADE = 1.15;
const ACTIVE_RANGE = 46; // world units from the plane before decoding starts

const VERTEX = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAGMENT = /* glsl */ `
  uniform sampler2D uMap;
  uniform float uOpacity;
  varying vec2 vUv;

  float hash2(vec2 q) {
    return fract(sin(dot(q, vec2(127.1, 311.7))) * 43758.5453);
  }
  float vnoise(vec2 q) {
    vec2 i = floor(q);
    vec2 f = fract(q);
    f = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash2(i), hash2(i + vec2(1.0, 0.0)), f.x),
      mix(hash2(i + vec2(0.0, 1.0)), hash2(i + vec2(1.0, 1.0)), f.x),
      f.y
    );
  }
  float fbm2(vec2 q) {
    return 0.5 * vnoise(q) + 0.25 * vnoise(q * 2.3 + 7.7) + 0.125 * vnoise(q * 4.9 + 3.1);
  }

  void main() {
    vec4 tex = texture2D(uMap, vUv);
    // Wispy, billowy silhouette at EVERY distance — and mathematically no
    // straight edge anywhere. Three layers:
    // 1) domain warp: all masks are sampled in noise-displaced coords, so
    //    even a falloff that runs along the plane boundary meanders;
    // 2) irregular off-center blob (noise-warped radius + uneven lobes);
    // 3) wavy containment forced to ZERO before the plane's physical edge
    //    (warp amplitude ±0.07 < 0.08 margin), so the rectangle boundary can
    //    never slice the cloud into a hard 180° line.
    // Plus large-scale internal density billows for close-range wispiness.
    vec2 w = vec2(fbm2(vUv * 4.0 + 13.7), fbm2(vUv * 4.0 + 71.3));
    vec2 q = vUv + (w - 0.5) * 0.14;

    vec2 p = q - vec2(0.47, 0.52); // off-center — never symmetric
    p.x *= 1.25;
    float r = length(p);
    float ang = atan(p.y, p.x);
    float wob =
      0.22 * (fbm2(q * 3.1) - 0.5) +
      0.05 * sin(ang * 3.0 + 1.7) +
      0.04 * sin(ang * 5.0 + 4.2);
    float blob = smoothstep(0.52, 0.22, r + wob);

    float contain =
      smoothstep(0.08, 0.24, q.x) * smoothstep(0.92, 0.76, q.x) *
      smoothstep(0.08, 0.26, q.y) * smoothstep(0.92, 0.74, q.y);

    float body = clamp(0.72 + 0.55 * fbm2(vUv * 2.2 + 41.0), 0.0, 1.0);

    float lum = dot(tex.rgb, vec3(0.299, 0.587, 0.114));
    float alpha = uOpacity * blob * contain * body * clamp(0.55 + lum * 0.9, 0.0, 1.0);
    gl_FragColor = vec4(tex.rgb, alpha);
  }
`;

function makeVideo(src) {
  const video = document.createElement('video');
  video.src = src;
  video.muted = true;
  video.playsInline = true;
  // cold until the camera nears the beat — .play() triggers the load then
  video.preload = 'none';
  video.loop = false;
  return video;
}

export function VideoNebula({ src = '/assets/video/work-loop.mp4', position = [0, 0, 0], width = 11 }) {
  const [aspect, setAspect] = useState(0.5625);
  const fade = useRef({ active: 0, fading: false, t: 0, playing: false });

  const videos = useMemo(() => [makeVideo(src), makeVideo(src)], [src]);
  const textures = useMemo(
    () =>
      videos.map((video) => {
        const texture = new VideoTexture(video);
        texture.colorSpace = SRGBColorSpace;
        return texture;
      }),
    [videos],
  );
  const uniforms = useMemo(
    () => textures.map((texture, i) => ({ uMap: { value: texture }, uOpacity: { value: i === 0 ? 1 : 0 } })),
    [textures],
  );

  useEffect(() => {
    const onMeta = () => {
      if (videos[0].videoWidth) setAspect(videos[0].videoHeight / videos[0].videoWidth);
    };
    videos[0].addEventListener('loadedmetadata', onMeta);
    return () => {
      videos[0].removeEventListener('loadedmetadata', onMeta);
      videos.forEach((v) => v.pause());
      textures.forEach((t) => t.dispose());
    };
  }, [videos, textures]);

  useFrame((state, dt) => {
    const st = fade.current;
    const distance = Math.abs(state.camera.position.z - position[2]);
    const shouldPlay = distance < ACTIVE_RANGE && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (shouldPlay && !st.playing) {
      st.playing = true;
      videos[st.active].play().catch(() => {});
    } else if (!shouldPlay && st.playing) {
      st.playing = false;
      videos.forEach((v) => v.pause());
      return;
    }
    if (!st.playing) return;

    const current = videos[st.active];
    const standby = videos[1 - st.active];

    if (!st.fading && current.duration && current.currentTime > current.duration - CROSSFADE) {
      st.fading = true;
      st.t = 0;
      standby.currentTime = 0;
      standby.play().catch(() => {});
    }
    if (st.fading) {
      st.t = Math.min(1, st.t + dt / CROSSFADE);
      uniforms[st.active].uOpacity.value = 1 - st.t;
      uniforms[1 - st.active].uOpacity.value = st.t;
      if (st.t >= 1) {
        current.pause();
        st.active = 1 - st.active;
        st.fading = false;
      }
    }
  });

  return (
    <group position={position}>
      {uniforms.map((u, i) => (
        <mesh key={i} position={[0, 0, i * 0.01]}>
          <planeGeometry args={[width, width * aspect]} />
          <shaderMaterial uniforms={u} vertexShader={VERTEX} fragmentShader={FRAGMENT} transparent depthWrite={false} />
        </mesh>
      ))}
    </group>
  );
}
